import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';
import { Translate, withLocalize } from 'react-localize-redux';
import { useParams } from 'react-router-dom';
import { PageHeader, PageWrapper } from 'components/partials';
import { Button, Spinner } from '../components/widgets';
import { sanitize } from '../components/utilities/Sanitize';
import toaster from '../utils/toaster';
import ReferenceGrid from './components/ReferenceGrid';
import ImportModal from './components/importModal/ImportModal';
import {
    getCampaigns,
    getDictionaries,
    getEvents,
    getReferenceTable,
    updateReferenceTable,
    uploadReferenceTableCsv,
} from './service';
import {
    applyFilters,
    buildCsv,
    buildOriginalSignatures,
    collapseRows,
    createEmptyRow,
    extractTableData,
    getDependentFields,
    getRowScopeKey,
    getVisibleColumns,
    hasActiveFilters,
    mergeDictionaryEntries,
    normalizeRow,
    toOptions,
} from './utils';
import {
    COLLAPSED_FIELDS,
    EVENT_FIELD,
    INITIAL_ROW_LIMIT,
    PER_ROW_DICTS,
    ROW_LIMIT_INCREMENT,
    MAX_ROWS_FOR_MANUAL_CHANGES,
    MAX_PHYSICAL_ROWS_TO_SAVE,
    SEARCH_DEBOUNCE_MS,
    SUCCESS_TOAST_AUTO_CLOSE,
    TRANSLATION_PREFIX,
    TABLE_FAQ_LINKS,
    VALUE_TOOLTIP_BY_SLUG,
} from './consts';
import css from './page.css';

// Campaigns take whatever the row has and narrow server side; events need its app.
const fetchRowDictionary = (field, row, query, tableName) =>
    field === EVENT_FIELD
        ? getEvents(row.app.name)
        : getCampaigns({
              appId: row.app ? row.app.name : null,
              source: row.source ? row.source.name : null,
              query,
              tableName,
          });

const dropPerRowDictionaries = dictionaries =>
    Object.keys(PER_ROW_DICTS).reduce((acc, field) => ({ ...acc, [field]: {} }), { ...dictionaries });

const toRowsWithFallback = (data, isValueCurrencyEnabled) =>
    data && data.length ? collapseRows(data.map(normalizeRow)) : [createEmptyRow(isValueCurrencyEnabled)];

// Shape a failed upload response into the modal's error-result view. Per-row failures carry the
// error list; whole-file failures (header mismatch, oversized/empty file) carry a single message
// instead of fabricated "0 of 0 rows" counts.
const buildImportErrorResult = (fileName, response) => {
    const value = response.value || {};
    if (value.errors && value.errors.length) {
        return {
            status: 'error',
            fileName,
            totalRows: value.total_rows || 0,
            errorRows: value.error_rows || value.errors.length,
            // total_errors may exceed the returned lines (the backend caps them); default to what we got.
            totalErrors: value.total_errors || value.errors.length,
            errors: value.errors,
        };
    }
    return {
        status: 'error',
        fileName,
        message: value.header_error || response.substatus || 'Import failed',
    };
};

function ReferenceTablePage({ translate }) {
    const { table_name: tableName } = useParams();

    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [updating, setUpdating] = useState(false);

    const [meta, setMeta] = useState({ title: '', description: '', isValueCurrencyEnabled: false, visibilityCols: [] });
    const [dictionaries, setDictionaries] = useState(null);
    // Fetches in flight, keyed `<field>:<cache key>` — without a spinner an in-flight campaign cell
    // reads as "this org has no campaigns".
    const [loadingDictionaries, setLoadingDictionaries] = useState({});
    const [rows, setRows] = useState([]);
    const [originalSignatures, setOriginalSignatures] = useState(() => new Set());
    const [errorsByKey, setErrorsByKey] = useState({});
    const [filters, setFilters] = useState({});
    const [rowLimit, setRowLimit] = useState(INITIAL_ROW_LIMIT);

    // CSV import modal: `importOpen` toggles it; `importResult` (null while on the upload view)
    // holds the success/error payload rendered on the result view.
    const [importOpen, setImportOpen] = useState(false);
    const [importResult, setImportResult] = useState(null);

    // Tracks the current slug so async responses (load/save/import) can be discarded if the user
    // navigated to a different table mid-flight — otherwise table A's data can land under table B.
    const tableNameRef = useRef(tableName);
    useEffect(() => {
        tableNameRef.current = tableName;
    }, [tableName]);

    const loadTable = useCallback(async () => {
        const requestedTable = tableName;
        const table = await getReferenceTable(requestedTable);
        if (tableNameRef.current !== requestedTable) {
            return;
        }
        const isValueCurrencyEnabled = !!table.is_value_currency_enabled;
        const nextRows = toRowsWithFallback(table.data, isValueCurrencyEnabled);
        setMeta({
            title: table.title,
            description: table.description,
            isValueCurrencyEnabled,
            visibilityCols: table.visibility_cols || [],
        });
        setRows(nextRows);
        setOriginalSignatures(buildOriginalSignatures(nextRows));
        setErrorsByKey({});
        setFilters({});
        setRowLimit(INITIAL_ROW_LIMIT);
    }, [tableName]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setLoadError(false);
            try {
                // Dictionaries are fetched once and reused; only the table reloads on a slug change.
                const dicts = dictionaries || (await getDictionaries());
                if (cancelled) {
                    return;
                }
                // Except the per-row ones: a campaign list is scoped to the table it was fetched for.
                requestedDictionaries.current = new Set();
                setDictionaries(dropPerRowDictionaries(dicts));
                await loadTable();
            } catch (error) {
                if (!cancelled) {
                    setLoadError(true);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableName]);

    const filtersActive = useMemo(() => hasActiveFilters(filters), [filters]);
    const displayedRows = useMemo(() => applyFilters(rows, filters), [rows, filters]);
    const visibleColumns = useMemo(() => getVisibleColumns(meta.visibilityCols), [meta.visibilityCols]);
    // The event column is only enabled on CPE tables, where every row must name one.
    const isEventRequired = useMemo(
        () => visibleColumns.some(column => column.field === EVENT_FIELD),
        [visibleColumns]
    );

    // Options are built once per dictionary, not per cell per keystroke: with up to 1000 rows that
    // was thousands of mappings per edit. Per-row entries are maps, so they map per cache key.
    const dictionaryOptions = useMemo(() => {
        if (!dictionaries) {
            return {};
        }
        return Object.keys(dictionaries).reduce((acc, key) => {
            const dictionary = dictionaries[key];
            acc[key] = Array.isArray(dictionary)
                ? toOptions(dictionary)
                : Object.keys(dictionary).reduce((byRowKey, rowKey) => {
                      byRowKey[rowKey] = toOptions(dictionary[rowKey]);
                      return byRowKey;
                  }, {});
            return acc;
        }, {});
    }, [dictionaries]);

    // Request ids already issued, so 600 rows sharing an app cost one request. A ref, not state:
    // nothing renders from it and it must be readable synchronously to dedupe concurrent opens.
    const requestedDictionaries = useRef(new Set());

    const loadRowDictionary = useCallback(
        async (field, row, query) => {
            const key = getRowScopeKey(field, row);
            if (key === null) {
                return; // Not scoped enough to ask yet - an event cell on a row with no app.
            }
            const cacheKey = `${field}:${key}`;
            // The events endpoint has no `q`, so typing in an event cell refetches nothing.
            const searchQuery = field === EVENT_FIELD ? null : query;
            const requestId = `${cacheKey}:${searchQuery || ''}`;
            if (requestedDictionaries.current.has(requestId)) {
                return;
            }
            requestedDictionaries.current.add(requestId);
            const requestedTable = tableName;
            setLoadingDictionaries(current => ({ ...current, [cacheKey]: true }));
            try {
                const entries = await fetchRowDictionary(field, row, searchQuery, requestedTable);
                if (tableNameRef.current !== requestedTable) {
                    return;
                }
                // Merge rather than replace: react-select narrows the visible list itself, and
                // validation then sees everything ever fetched, so a search can't invalidate a
                // saved campaign.
                setDictionaries(current => ({
                    ...current,
                    [field]: {
                        ...current[field],
                        [key]: mergeDictionaryEntries((current[field] || {})[key], entries),
                    },
                }));
            } catch (error) {
                // Leave it unset (so nothing reds out) and let the next open retry.
                requestedDictionaries.current.delete(requestId);
            } finally {
                setLoadingDictionaries(current => ({ ...current, [cacheKey]: false }));
            }
        },
        [tableName]
    );

    // Debounced separately from the open path, so opening a cell stays immediate.
    const searchRowDictionary = useMemo(
        () => debounce((field, row, query) => loadRowDictionary(field, row, query), SEARCH_DEBOUNCE_MS),
        [loadRowDictionary]
    );
    useEffect(() => () => searchRowDictionary.cancel(), [searchRowDictionary]);

    const perRowDictionaries = useMemo(
        () => ({
            loading: loadingDictionaries,
            onOpen: loadRowDictionary,
            onSearch: searchRowDictionary,
        }),
        [loadingDictionaries, loadRowDictionary, searchRowDictionary]
    );

    const updateRow = useCallback((key, updater) => {
        setRows(current => current.map(row => (row._key === key ? updater(row) : row)));
    }, []);

    const clearRowFieldError = useCallback((key, field) => {
        setErrorsByKey(current => {
            if (!current[key] || !current[key][field]) {
                return current;
            }
            const nextRowErrors = { ...current[key] };
            delete nextRowErrors[field];
            const next = { ...current };
            if (Object.keys(nextRowErrors).length) {
                next[key] = nextRowErrors;
            } else {
                delete next[key];
            }
            return next;
        });
    }, []);

    const onCellChange = useCallback(
        (key, field, option, dictionary) => {
            if (COLLAPSED_FIELDS.includes(field)) {
                const list = (option || []).map(opt => {
                    const found = (dictionary || []).find(entry => entry.name === opt.value);
                    return found || { name: opt.value, display_name: opt.label };
                });
                updateRow(key, row => ({ ...row, country: list }));
                clearRowFieldError(key, field);
                return;
            }
            // A per-row dictionary can still be unfetched, hence the fallback.
            const resolved = option
                ? (dictionary || []).find(entry => entry.name === option.value) || {
                      name: option.value,
                      display_name: option.label,
                  }
                : null;
            // A new scope serves a different list, so its dependent cells no longer hold. Only on a
            // real change, so re-picking the same option does not wipe the row.
            const dependents = getDependentFields(field);
            updateRow(key, row => {
                const changed = (row[field] ? row[field].name : null) !== (resolved ? resolved.name : null);
                const next = { ...row, [field]: resolved };
                if (changed) {
                    dependents.forEach(dependent => {
                        next[dependent] = null;
                    });
                }
                return next;
            });
            clearRowFieldError(key, field);
            dependents.forEach(dependent => clearRowFieldError(key, dependent));
        },
        [updateRow, clearRowFieldError]
    );

    const onDateChange = useCallback(
        (key, startDate) => updateRow(key, row => ({ ...row, startDate: startDate || null })),
        [updateRow]
    );

    const onValueChange = useCallback(
        (key, value) => {
            updateRow(key, row => ({ ...row, value }));
            clearRowFieldError(key, 'value');
        },
        [updateRow, clearRowFieldError]
    );

    const onFilterChange = useCallback((field, query) => {
        setFilters(current => ({ ...current, [field]: query }));
    }, []);

    const onLoadMore = useCallback(() => setRowLimit(limit => limit + ROW_LIMIT_INCREMENT), []);

    const addRow = useCallback(() => {
        // Guard `updating` too: the button is disabled during save/import, but Ctrl+N calls this directly.
        if (filtersActive || updating) {
            return;
        }
        setRows(current => [createEmptyRow(meta.isValueCurrencyEnabled), ...current]);
        setRowLimit(INITIAL_ROW_LIMIT);
    }, [filtersActive, updating, meta.isValueCurrencyEnabled]);

    const deleteRow = useCallback(
        key => {
            if (filtersActive || updating) {
                return;
            }
            setRows(current => {
                const next = current.filter(row => row._key !== key);
                return next.length ? next : [createEmptyRow(meta.isValueCurrencyEnabled)];
            });
        },
        [filtersActive, updating, meta.isValueCurrencyEnabled]
    );

    const saveTable = useCallback(async () => {
        if (filtersActive || updating) {
            return;
        }
        const { payload, errorsByKey: nextErrors, valid, hasInvalidDate, hasInvalidValue } = extractTableData({
            rows,
            originalSignatures,
            dictionaries,
            isValueCurrencyEnabled: meta.isValueCurrencyEnabled,
            isEventRequired,
        });

        if (hasInvalidDate) {
            toaster.error(translate(`${TRANSLATION_PREFIX}.INVALID_DATES`));
            return;
        }
        if (rows.length > MAX_ROWS_FOR_MANUAL_CHANGES) {
            toaster.error(translate(`${TRANSLATION_PREFIX}.TOO_LARGE_TO_SAVE`, { max: MAX_ROWS_FOR_MANUAL_CHANGES }));
            return;
        }
        if (payload.length > MAX_PHYSICAL_ROWS_TO_SAVE) {
            toaster.error(translate(`${TRANSLATION_PREFIX}.TOO_MANY_TOTAL_ROWS`, { max: MAX_PHYSICAL_ROWS_TO_SAVE }));
            return;
        }
        setErrorsByKey(nextErrors);
        if (hasInvalidValue) {
            toaster.error(translate(`${TRANSLATION_PREFIX}.INVALID_VALUE`));
            return;
        }
        if (!valid) {
            toaster.error(translate(`${TRANSLATION_PREFIX}.INVALID_DATA`));
            return;
        }

        const requestedTable = tableName;
        setUpdating(true);
        try {
            const refreshed = await updateReferenceTable(requestedTable, payload);
            if (tableNameRef.current !== requestedTable) {
                return;
            }
            if (refreshed && refreshed.data && refreshed.data.length) {
                const nextRows = collapseRows(refreshed.data.map(normalizeRow));
                setRows(nextRows);
                setOriginalSignatures(buildOriginalSignatures(nextRows));
                setErrorsByKey({});
            }
            toaster.success(translate(`${TRANSLATION_PREFIX}.SAVE_SUCCESS`), { autoClose: SUCCESS_TOAST_AUTO_CLOSE });
        } catch (error) {
            toaster.error(translate(`${TRANSLATION_PREFIX}.SAVE_ERROR`));
        } finally {
            setUpdating(false);
        }
    }, [filtersActive, updating, rows, originalSignatures, dictionaries, meta.isValueCurrencyEnabled, isEventRequired,
        tableName, translate]);

    const exportCsv = useCallback(() => {
        const csv = buildCsv({
            rows: displayedRows,
            visibleColumns,
            isValueCurrencyEnabled: meta.isValueCurrencyEnabled,
        });
        const blob = new Blob([csv], { type: 'text/csv' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `${meta.title}.csv`;
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);
        }, 100);
    }, [displayedRows, visibleColumns, meta.isValueCurrencyEnabled, meta.title]);

    const importCsv = useCallback(
        async file => {
            if (!file) {
                return;
            }
            if (file.name.split('.').pop().toLowerCase() !== 'csv') {
                toaster.error(translate(`${TRANSLATION_PREFIX}.INVALID_FILE_TYPE`));
                return;
            }
            const requestedTable = tableName;
            setUpdating(true);
            try {
                const response = await uploadReferenceTableCsv(file, requestedTable);
                if (tableNameRef.current !== requestedTable) {
                    return;
                }
                if (response.status === 0) {
                    const value = response.value || {};
                    const rowsImported = value.rows_imported ?? 0;
                    setImportResult({
                        status: 'success',
                        fileName: file.name,
                        rowsImported,
                        totalRows: value.total_rows ?? rowsImported,
                    });
                    await loadTable();
                } else {
                    // Structured per-row errors live in `value`; message fallback in `substatus`.
                    setImportResult(buildImportErrorResult(file.name, response));
                }
            } catch (error) {
                toaster.error(translate(`${TRANSLATION_PREFIX}.IMPORT_UPLOAD_ERROR`));
            } finally {
                setUpdating(false);
            }
        },
        [tableName, translate, loadTable]
    );

    const openImportModal = useCallback(() => {
        setImportResult(null);
        setImportOpen(true);
    }, []);

    const closeImportModal = useCallback(() => {
        setImportOpen(false);
        setImportResult(null);
    }, []);

    // Keyboard shortcuts: Ctrl+S saves, Ctrl+N adds a row (read latest handlers via refs).
    const saveRef = useRef(saveTable);
    saveRef.current = saveTable;
    const addRowRef = useRef(addRow);
    addRowRef.current = addRow;
    useEffect(() => {
        const onKeyDown = event => {
            if (!event.ctrlKey && !event.metaKey) {
                return;
            }
            if (event.key === 's') {
                event.preventDefault();
                saveRef.current();
            } else if (event.key === 'n') {
                event.preventDefault();
                addRowRef.current();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    if (loading) {
        return <Spinner show expanded />;
    }

    if (loadError) {
        return (
            <PageWrapper>
                <div className={css.generalError}>
                    <Translate id={`${TRANSLATION_PREFIX}.GENERAL_ERROR`} />
                </div>
            </PageWrapper>
        );
    }

    const faqLink = TABLE_FAQ_LINKS[meta.title];
    const valueTooltip = VALUE_TOOLTIP_BY_SLUG[tableName] || `${TRANSLATION_PREFIX}.VALUE_TOOLTIP.DEFAULT`;
    const filtersTooltipKey = filtersActive ? `${TRANSLATION_PREFIX}.REMOVE_FILTERS` : undefined;

    return (
        <PageWrapper className={css.wrapper}>
            <PageHeader
                text={`${TRANSLATION_PREFIX}.PAGE_TITLE`}
                textReplace={{ title: meta.title }}
                subText={{ faqLink: faqLink || '' }}
            />
            {!faqLink && meta.description && (
                // Sanitize the (org-authored) description to strip scripts/handlers before rendering.
                // eslint-disable-next-line react/no-danger
                <p className={css.description} dangerouslySetInnerHTML={{ __html: sanitize(meta.description) }} />
            )}
            <div className={css.toolbarRow}>
                <div className={css.toolbar}>
                    <div className={css.ioButtons}>
                        <Button
                            type="secondary"
                            text={`${TRANSLATION_PREFIX}.EXPORT`}
                            disabled={updating}
                            onClick={exportCsv}
                        />
                        <Button
                            type="secondary"
                            text={`${TRANSLATION_PREFIX}.IMPORT`}
                            disabled={updating}
                            onClick={openImportModal}
                        />
                    </div>
                    <div className={css.editButtons}>
                        <Button
                            type="secondary"
                            text={`${TRANSLATION_PREFIX}.ADD_ROW`}
                            disabled={updating || filtersActive}
                            tooltipKey={filtersTooltipKey}
                            onClick={addRow}
                        />
                        <Button
                            type="primary"
                            text={`${TRANSLATION_PREFIX}.SAVE_CHANGES`}
                            disabled={updating || filtersActive}
                            tooltipKey={filtersTooltipKey}
                            onClick={saveTable}
                        />
                    </div>
                </div>
            </div>

            <ReferenceGrid
                rows={displayedRows}
                visibleColumns={visibleColumns}
                dictionaries={dictionaries}
                dictionaryOptions={dictionaryOptions}
                perRowDictionaries={perRowDictionaries}
                isValueCurrencyEnabled={meta.isValueCurrencyEnabled}
                valueTooltip={valueTooltip}
                errorsByKey={errorsByKey}
                filters={filters}
                rowLimit={rowLimit}
                disabled={updating}
                deleteDisabled={updating || filtersActive}
                onFilterChange={onFilterChange}
                onLoadMore={onLoadMore}
                onCellChange={onCellChange}
                onDateChange={onDateChange}
                onValueChange={onValueChange}
                onDeleteRow={deleteRow}
            />

            <ImportModal
                open={importOpen}
                translate={translate}
                visibleColumns={visibleColumns}
                isValueCurrencyEnabled={meta.isValueCurrencyEnabled}
                helpLink={faqLink}
                uploading={updating}
                result={importResult}
                onImport={importCsv}
                onClose={closeImportModal}
                onBackToUpload={() => setImportResult(null)}
            />
        </PageWrapper>
    );
}

ReferenceTablePage.propTypes = {
    translate: PropTypes.func.isRequired,
};

export default withLocalize(ReferenceTablePage);
